import User from '@/store/models/user/User'

/**
 * Public collection endpoints return relationship-named fields (owner, tags,
 * addedBy) that clash with Vuex ORM's belongsTo/hasManyBy normalization, so they
 * are pulled out of the spread and the explicit foreign keys set instead.
 *
 * `addedBy` arrives populated as { _id, username, avatar } from the single-item
 * endpoint and as a raw id from the list endpoints — take the id either way, or
 * `addedById` holds an object and the relation never resolves.
 */
export function normalizePublicCollectionItem(item, collectionId, extra = {}) {
  const { owner, tags, addedBy, ...rest } = item
  return {
    ...rest,
    id: item._id,
    ownerId: collectionId,
    addedById: addedBy?._id ?? addedBy ?? null,
    tagIds: tags?.map(t => t._id) || [],
    tags: tags?.map(t => ({ ...t, id: t._id, ownerId: t.owner })) || [],
    ...extra,
  }
}

/**
 * The populated posters carried on public items, ready for User.insertOrUpdate.
 * normalizePublicCollectionItem strips `addedBy` off the item payload, so
 * without inserting these the User entity stays empty, `.with('addedBy')`
 * resolves to null and the poster header renders as a blank avatar.
 */
export function collectPublicItemPosters(items) {
  return (items || []).map(i => i.addedBy).filter(u => u && typeof u === 'object')
}

/** Inserts those posters. Runs before the items, which no longer carry them. */
export async function insertPublicItemPosters(items) {
  const users = collectPublicItemPosters(items)
  if (users.length) await User.insertOrUpdate({ data: users })
}
